/* The Commissioner's Guide (2026-09-30).
   Progressive extras only: every article, link and image works without this
   file. Custom elements set themselves up when they appear, including after a
   theme-editor section reload, and clean up when removed. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wide = () => window.matchMedia('(min-width: 1100px)').matches;

  /* Warm motes rising behind the masthead and the closing band. */
  if (!customElements.get('ffb-embers')) {
    const TINTS = ['233,194,94', '242,217,142', '255,176,92'];
    customElements.define('ffb-embers', class extends HTMLElement {
      connectedCallback() {
        if (this.ready || reduceMotion()) return;
        this.ready = true;
        const canvas = document.createElement('canvas');
        this.append(canvas);
        const ctx = canvas.getContext('2d');
        const density = parseFloat(this.dataset.density || '1');
        let w = 0, h = 0, motes = [], raf = 0, last = 0, onScreen = false;
        const spawn = (m, anywhere) => {
          m.x = Math.random() * w;
          m.y = anywhere ? Math.random() * h : h + 8;
          m.r = .6 + Math.random() * 1.7;
          m.vy = 9 + Math.random() * 20;
          m.sway = 6 + Math.random() * 14;
          m.freq = .4 + Math.random() * .9;
          m.phase = Math.random() * 6.3;
          m.max = .25 + Math.random() * .45;
          m.tint = TINTS[(Math.random() * TINTS.length) | 0];
          m.age = anywhere ? 2 : 0;
          return m;
        };
        const size = () => {
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          w = this.clientWidth; h = this.clientHeight;
          canvas.width = Math.max(1, w * dpr); canvas.height = Math.max(1, h * dpr);
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          const count = Math.round(Math.min(32, Math.max(9, w / 46)) * density);
          while (motes.length < count) motes.push(spawn({}, true));
          motes.length = count;
        };
        const frame = t => {
          raf = requestAnimationFrame(frame);
          const dt = Math.min(.05, (t - (last || t)) / 1000); last = t;
          ctx.clearRect(0, 0, w, h);
          for (const m of motes) {
            m.age += dt;
            m.y -= m.vy * dt;
            const x = m.x + Math.sin(t / 1000 * m.freq + m.phase) * m.sway;
            if (m.y < -10) spawn(m, false);
            const fadeIn = Math.min(1, m.age / 1.4);
            const fadeOut = Math.min(1, Math.max(0, m.y / (h * .35)));
            const a = m.max * fadeIn * fadeOut;
            if (a <= .01) continue;
            ctx.fillStyle = `rgba(${m.tint},${(a * .22).toFixed(3)})`;
            ctx.beginPath(); ctx.arc(x, m.y, m.r * 3.4, 0, 6.2832); ctx.fill();
            ctx.fillStyle = `rgba(${m.tint},${a.toFixed(3)})`;
            ctx.beginPath(); ctx.arc(x, m.y, m.r, 0, 6.2832); ctx.fill();
          }
        };
        const run = () => { if (!raf && onScreen && !document.hidden) { last = 0; raf = requestAnimationFrame(frame); } };
        const stop = () => { cancelAnimationFrame(raf); raf = 0; };
        this.resize = new ResizeObserver(size);
        this.resize.observe(this);
        this.seen = new IntersectionObserver(entries => { onScreen = entries[0].isIntersecting; onScreen ? run() : stop(); });
        this.seen.observe(this);
        this.vis = () => (document.hidden ? stop() : run());
        document.addEventListener('visibilitychange', this.vis);
        this.stop = stop;
        size();
      }
      disconnectedCallback() {
        if (!this.ready) return;
        this.stop(); this.resize.disconnect(); this.seen.disconnect();
        document.removeEventListener('visibilitychange', this.vis);
        this.ready = false;
      }
    });
  }

  /* Reading progress: a thin gold line at the top, and the rail's fill. */
  if (!customElements.get('ffb-progress')) {
    customElements.define('ffb-progress', class extends HTMLElement {
      connectedCallback() {
        const prose = document.querySelector('[data-ffb-post] .ffb-prose');
        if (!prose || this.onScroll) return;
        let queued = false;
        const update = () => {
          queued = false;
          const box = prose.getBoundingClientRect();
          const span = Math.max(1, box.height - window.innerHeight * .55);
          const read = Math.min(1, Math.max(0, (window.innerHeight * .25 - box.top) / span));
          this.style.setProperty('--ffb-read', read.toFixed(4));
          const toc = document.querySelector('.ffb-toc');
          if (toc) toc.style.setProperty('--ffb-read', read.toFixed(4));
        };
        this.onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        update();
      }
      disconnectedCallback() {
        if (!this.onScroll) return;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.onScroll = null;
      }
    });
  }

  /* Contents, tables and tap-to-enlarge art for the article body. */
  const slug = (text, i) => (text.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)) || `section-${i + 1}`;

  const largest = img => {
    const src = img.currentSrc || img.src;
    try {
      const url = new URL(src, location.href);
      if (/shopify|\/cdn\/shop\//.test(url.href)) { url.searchParams.set('width', '2000'); url.searchParams.delete('height'); url.searchParams.delete('crop'); }
      return url.href;
    } catch (e) { return src; }
  };

  let box = null, opener = null;
  const lightbox = () => {
    if (box) return box;
    box = document.createElement('dialog');
    box.className = 'ffb-lightbox';
    box.setAttribute('aria-label', 'Enlarged image');
    box.innerHTML = '<figure class="ffb-lightbox__fig"><img alt=""><figcaption hidden></figcaption></figure>' +
      '<button type="button" class="ffb-lightbox__close" aria-label="Close the enlarged image"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.3 2 8 6.7 12.7 2 14 3.3 9.3 8l4.7 4.7-1.3 1.3L8 9.3 3.3 14 2 12.7 6.7 8 2 3.3z"/></svg></button>';
    box.querySelector('button').addEventListener('click', () => box.close());
    box.addEventListener('click', e => { if (e.target === box) box.close(); });
    box.addEventListener('close', () => { if (opener && opener.isConnected) opener.focus({ preventScroll: true }); opener = null; });
    document.body.append(box);
    return box;
  };
  const enlarge = (img, trigger) => {
    const dialog = lightbox();
    const big = dialog.querySelector('img');
    const cap = dialog.querySelector('figcaption');
    big.src = largest(img);
    big.alt = img.alt || '';
    const fig = img.closest('figure');
    const text = (fig && fig.querySelector('figcaption') ? fig.querySelector('figcaption').textContent : img.alt || '').trim();
    cap.textContent = text; cap.hidden = !text;
    opener = trigger;
    dialog.showModal();
  };

  const HINT = '<span class="ffb-zoom__hint" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M6.5 1a5.5 5.5 0 0 1 4.4 8.8l3.8 3.8-1.1 1.1-3.8-3.8A5.5 5.5 0 1 1 6.5 1Zm0 1.6a3.9 3.9 0 1 0 0 7.8 3.9 3.9 0 0 0 0-7.8Zm.8 1.6v1.5h1.5v1.6H7.3v1.5H5.7V7.3H4.2V5.7h1.5V4.2Z"/></svg></span>';

  const enhance = prose => {
    prose.querySelectorAll('table').forEach(table => {
      if (table.parentElement.classList.contains('ffb-table')) return;
      const wrap = document.createElement('div');
      wrap.className = 'ffb-table';
      wrap.tabIndex = 0;
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-label', 'Table');
      table.before(wrap); wrap.append(table);
    });
    prose.querySelectorAll('tr').forEach(row => { if (!row.textContent.trim() && !row.querySelector('img')) row.remove(); });
    prose.querySelectorAll('img').forEach(img => {
      if (img.closest('a, button, .ffb-zoom')) return;
      const known = Number(img.getAttribute('width')) || img.naturalWidth || 0;
      if (known && known < 320) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ffb-zoom';
      btn.setAttribute('aria-label', img.alt ? `Enlarge image: ${img.alt}` : 'Enlarge image');
      img.before(btn); btn.append(img); btn.insertAdjacentHTML('beforeend', HINT);
      btn.addEventListener('click', () => enlarge(img, btn));
    });
  };

  if (!customElements.get('ffb-toc')) {
    customElements.define('ffb-toc', class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        const post = this.closest('[data-ffb-post]');
        const prose = post && post.querySelector('.ffb-prose');
        if (!prose) return;
        this.ready = true;
        enhance(prose);
        const heads = [...prose.querySelectorAll('h2')].filter(h => !h.closest('.toc') && h.textContent.trim().length > 1);
        const read = post.querySelector('.ffb-read');
        if (heads.length < 3) { if (read) read.classList.add('ffb-read--no-toc'); return; }
        const list = this.querySelector('ol');
        const used = new Set();
        const links = heads.map((h, i) => {
          if (!h.id) { let id = slug(h.textContent.trim(), i); while (used.has(id) || document.getElementById(id)) id += '-2'; h.id = id; }
          used.add(h.id);
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${h.id}`;
          a.textContent = h.textContent.trim().replace(/\s+/g, ' ');
          li.append(a); list.append(li);
          return a;
        });
        this.hidden = false;
        post.classList.add('ffb-post--toc');
        /* an article's own contents box repeats this list; hide the outermost block made of jump links */
        [...prose.querySelectorAll('div, nav, section, aside, details, ul, ol')].forEach(el => {
          const jumps = el.querySelectorAll('a[href^="#"]').length;
          const all = el.querySelectorAll('a[href]').length;
          if (jumps < 3 || jumps / all < .8 || el.textContent.length > 1600 || el.closest('.ffb-dup-toc')) return;
          let top = el;
          while (top.parentElement && top.parentElement !== prose && top.parentElement.querySelectorAll('a[href^="#"]').length === jumps && top.parentElement.textContent.length < 1800) top = top.parentElement;
          top.classList.add('ffb-dup-toc');
        });
        const details = this.querySelector('details');
        const summary = this.querySelector('summary');
        details.open = wide();
        /* on desktop the summary is only a label, so it leaves the tab order */
        const fit = () => { summary.tabIndex = wide() ? -1 : 0; if (wide()) details.open = true; };
        fit();
        this.mq = window.matchMedia('(min-width: 1100px)');
        this.mq.addEventListener('change', fit);
        details.addEventListener('toggle', () => { if (wide() && !details.open) details.open = true; });
        summary.addEventListener('click', e => { if (wide()) e.preventDefault(); });
        list.addEventListener('click', e => { if (e.target.closest('a') && !wide()) details.open = false; });
        const mark = a => links.forEach(l => (l === a ? l.setAttribute('aria-current', 'true') : l.removeAttribute('aria-current')));
        this.spy = new IntersectionObserver(entries => {
          const hit = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (hit) mark(links[heads.indexOf(hit.target)]);
        }, { rootMargin: '-15% 0px -70% 0px' });
        heads.forEach(h => this.spy.observe(h));
      }
      disconnectedCallback() { if (this.spy) this.spy.disconnect(); this.ready = false; }
    });
  }
})();
